import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, MapPinned } from "lucide-react";
import { ASSETS } from "@/lib/api";
import SuggestRouteModal from "@/components/rotta/SuggestRouteModal";

const links = [
  { to: "/", label: "Home" },
  { to: "/rotas", label: "Rotas" },
  { to: "/cidades", label: "Cidades" },
  { to: "/eventos", label: "Eventos" },
  { to: "/blog", label: "Blog" },
  { to: "/parcerias", label: "Parcerias" },
  { to: "/sobre", label: "Sobre" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 24);
    handler();
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || open ? "bg-[#0B0B0B]/95 backdrop-blur-md border-b border-[#1f1f1f]" : "bg-transparent"
      }`}
    >
      <div className="container-rotta flex items-center justify-between gap-6 xl:gap-10 h-[76px]">
        <Link to="/" data-testid="navbar-logo-link" className="flex items-center gap-3 shrink-0">
          <img src={ASSETS.logoMark} alt="ROTTA" className="h-9 w-auto" />
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display text-xl tracking-wider text-white">ROTTA</span>
            <span className="text-[10px] tracking-[0.3em] text-[#C6A762]">VIVA A ESTRADA</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 xl:gap-3 flex-1 justify-center">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-2.5 xl:px-3.5 py-2 text-[11px] xl:text-xs tracking-[0.15em] uppercase transition-colors whitespace-nowrap ${
                  isActive ? "text-[#C6A762]" : "text-white/80 hover:text-[#C6A762]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2 xl:gap-3 shrink-0">
          <button
            type="button"
            onClick={() => setSuggestOpen(true)}
            data-testid="navbar-cta-indicar"
            className="inline-flex items-center gap-2 px-3.5 xl:px-4 py-2.5 rounded-full text-[10px] xl:text-[11px] tracking-[0.18em] uppercase text-[#C6A762] border border-[#C6A762]/40 hover:border-[#C6A762] hover:bg-[#C6A762]/5 transition-colors whitespace-nowrap"
          >
            <MapPinned size={14} /> Indique uma rota
          </button>
          <Link
            to="/rotas"
            data-testid="navbar-cta-explorar"
            className="btn-gold px-4 xl:px-5 py-2.5 rounded-full text-[11px] xl:text-xs whitespace-nowrap"
          >
            Explorar Rotas
          </Link>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden p-2 text-white"
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#1f1f1f] bg-[#0B0B0B]/98 backdrop-blur-md">
          <nav className="container-rotta py-4 flex flex-col">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`nav-link-mobile-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `py-3 text-sm tracking-wider uppercase border-b border-[#1a1a1a] last:border-0 ${
                    isActive ? "text-[#C6A762]" : "text-white/90"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <button
              type="button"
              onClick={() => { setOpen(false); setSuggestOpen(true); }}
              data-testid="navbar-cta-indicar-mobile"
              className="mt-4 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-[11px] tracking-[0.2em] uppercase text-[#C6A762] border border-[#C6A762]/40 hover:border-[#C6A762] hover:bg-[#C6A762]/5 transition-colors"
            >
              <MapPinned size={14} /> Indique uma rota
            </button>
            <Link
              to="/rotas"
              onClick={() => setOpen(false)}
              className="btn-gold text-center mt-2 px-5 py-3 rounded-full text-xs"
            >
              Explorar Rotas
            </Link>
          </nav>
        </div>
      )}

      <SuggestRouteModal open={suggestOpen} onClose={() => setSuggestOpen(false)} />
    </header>
  );
}
